import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSearch, MdArrowForward } from 'react-icons/md';
import API from '../../services/api';
import { LogoMark } from './ResearchPage';

// The master surface's way in: search a company, open everything ResearchOS holds for it.
//
// The type-ahead index is the set of SCORED names (from the scores endpoint) — the names that
// actually have a profile worth opening. A name known only to Chatter is not in the list, but the
// box still accepts a free-typed ticker on Enter and lets the company endpoint answer (it 404s
// cleanly if nothing is held), so the search is never a hard gate on the index — it is a
// convenience over it. Deterministic match only: case-insensitive substring on ticker or name, no
// fuzzy ranking, in keeping with the house rule.

const norm = (s) => String(s || '').toLowerCase();

export default function CompanySearch() {
  const nav = useNavigate();
  const [index, setIndex] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    let alive = true;
    API.get('/api/research-feed/scores/')
      .then((r) => {
        if (!alive) return;
        // One row per ticker (the endpoint returns latest-per-team; a name scored by two teams
        // must appear once in search). First occurrence wins — it carries the name and sector.
        const seen = new Set(); const rows = [];
        (r.data?.items || []).forEach((it) => {
          const t = (it.ticker || '').toUpperCase();
          if (!t || seen.has(t)) return;
          seen.add(t);
          rows.push({ ticker: t, name: it.security_name || '', sector: it.sector || '' });
        });
        rows.sort((a, b) => a.ticker.localeCompare(b.ticker));
        setIndex(rows);
      })
      .catch(() => { /* the free-typed Enter path still works with an empty index */ });
    return () => { alive = false; };
  }, []);

  // Close the dropdown on an outside click.
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const matches = useMemo(() => {
    const needle = norm(q).trim();
    if (!needle) return index.slice(0, 8);
    return index
      .filter((r) => norm(r.ticker).includes(needle) || norm(r.name).includes(needle))
      .slice(0, 8);
  }, [q, index]);

  const go = (ticker) => {
    const t = (ticker || '').trim().toUpperCase();
    if (t) nav(`/research/company/${encodeURIComponent(t)}`);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      // The highlighted match, else the first match, else whatever was typed (free ticker).
      const pick = matches[hi] || matches[0];
      go(pick ? pick.ticker : q);
    } else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 focus-within:border-brand-gold dark:border-gray-800 dark:bg-[#161b22]">
        <MdSearch size={20} className="shrink-0 text-gray-400" />
        <input
          type="text" value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search a company — ticker or name"
          aria-label="Search a company"
          className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none dark:text-gray-100"
        />
        {q
          ? <button type="button" onClick={() => go(q)} aria-label="Open"
                    className="shrink-0 rounded-lg p-1 text-gray-400 hover:text-brand-gold">
              <MdArrowForward size={18} />
            </button>
          : null}
      </div>

      {open && matches.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-800 dark:bg-[#161b22]">
          {matches.map((m, i) => (
            <li key={m.ticker}>
              <button
                type="button"
                onMouseEnter={() => setHi(i)}
                onClick={() => go(m.ticker)}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${i === hi
                  ? 'bg-brand-gold/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}
              >
                <LogoMark ticker={m.ticker} size={26} />
                <span className="min-w-0 flex-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{m.ticker}</span>
                  {m.name ? <span className="ml-2 text-xs text-gray-400">{m.name}</span> : null}
                </span>
                {m.sector ? <span className="shrink-0 text-[10px] text-gray-400">{m.sector}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
